import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarCellProps } from '@planne-software/uni/DatePicker';

import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import {
  adderResumeSelector,
  productSelector,
  tariffsState,
  selectAvailableDate,
  goToNextStep,
} from '@src/stores/product';
import { appInfoSelector } from '@src/stores/app';
import { MonthTuple, dateToObj, mergeIntervalWithDates, mountInterval, newIntervalDate } from '@src/common/calendar';
import { track } from '@src/services/analytics';

import { buildLegend, LegendModel, classifyDay, useScheduleDatesLoader } from '@src/domains/scheduling';
import Cell from '@src/domains/scheduling/components/Cell';
import { trackStepConfirmed } from '@src/domains/products/features/product-adder-v2/analytics/widgetTracking';

export type UseDateSelectorReturn = {
  placeholder: string;
  selectedDateFormatted: string | null;
  disabled: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;

  pickerValue: Date | null;
  referenceDate: Date;
  pending: boolean;
  onMonthChange: (firstVisibleDate: Date) => void;
  onSelectDate: (date: Date) => void;
  onConfirm: () => void;
  dayRenderer: (props: CalendarCellProps) => React.ReactNode;
  legend: LegendModel;
  totalLabel: string | null;
};

const tupleToDate = ([year, month]: MonthTuple): Date => new Date(year, month - 1, 1);

const formatDateString = (date: Date): string => format(date, 'yyyy-MM-dd');

export function useDateSelector(): UseDateSelectorReturn {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();

  const { productId } = useAppSelector(adderResumeSelector(widgetId));
  const product = useAppSelector(productSelector(productId));
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const featuredTariffPreviewMode = useAppSelector(appInfoSelector)?.featuredTariffPreviewMode ?? null;

  const parsedSelectedDate = useMemo(() => {
    if (!selectedDate) return null;
    return parseISO(selectedDate);
  }, [selectedDate]);

  const [open, setOpen] = useState(false);
  // Local draft committed to Redux only on `onConfirm`. Resynced from the
  // committed value while the popover is closed so external resets/selections
  // show up on next open.
  const [draftDate, setDraftDate] = useState<Date | null>(parsedSelectedDate);

  useEffect(() => {
    if (open) return;
    setDraftDate(parsedSelectedDate);
  }, [open, parsedSelectedDate]);

  // Shared loading state machine (monthTuple + bounded auto-load + per-product fetch).
  // Auto-load is disabled once the user has a selected date so we honor their choice and
  // don't drift the visible month away from it.
  const {
    monthTuple: currentMonthTuple,
    setMonthTuple: setCurrentMonthTuple,
    dates,
    pending,
  } = useScheduleDatesLoader({
    productId,
    seedDate: parsedSelectedDate,
    enableAutoLoad: !parsedSelectedDate,
    // Hold off fetching until the product object has actually loaded into state — avoids firing
    // a request during the loading window. The parent doesn't render this selector for
    // without-date, and the loadAvailableDates thunk no-ops for it, so this needn't know the mode.
    enabled: Boolean(product),
  });

  const placeholder = i18n('adder_step_calendar');

  const selectedDateFormatted = useMemo(() => {
    if (!parsedSelectedDate) return null;
    return format(parsedSelectedDate, 'dd/MM/yyyy');
  }, [parsedSelectedDate]);

  const legend = useMemo(
    () =>
      buildLegend({
        showPriceDisclaimer: Boolean(featuredTariffPreviewMode),
        i18n: i18n as (key: string) => string,
      }),
    [featuredTariffPreviewMode, i18n],
  );

  // Merged interval for the *visible* month — used by dayRenderer for every cell.
  // Memoized so we don't rebuild the array ~42 times per render inside the callback.
  const mergedVisibleMonth = useMemo(() => {
    const interval = mountInterval(currentMonthTuple);
    return mergeIntervalWithDates(interval, dates ?? {});
  }, [currentMonthTuple, dates]);

  const totalLabel = useMemo<string | null>(() => {
    if (!draftDate) return null;

    const [y, m] = dateToObj(draftDate);
    const selectedMonthTuple: MonthTuple = [y, m];
    const interval = mountInterval(selectedMonthTuple);
    const merged = mergeIntervalWithDates(interval, dates ?? {});
    const dateString = formatDateString(draftDate);
    const match = merged.find((entry) => entry.date.dateString === dateString);

    if (!match) return null;
    const classification = classifyDay({
      intervalDate: match.date,
      availables: match.availableSchedulings,
      unavailables: match.unavailableSchedulings,
      tariffPreviewMode: featuredTariffPreviewMode,
    });

    if (!classification.price) return null;

    return `${classification.price.currency} ${classification.price.priceStr}`;
  }, [draftDate, dates, featuredTariffPreviewMode]);

  const onMonthChange = useCallback(
    (firstVisibleDate: Date) => {
      const [y, m] = dateToObj(firstVisibleDate);
      setCurrentMonthTuple([y, m]);
    },
    [setCurrentMonthTuple],
  );

  // Day clicks stay local — Redux side effects (tariff remount, hour reset,
  // language reconciliation) only run on confirm, so a stray click can't
  // clobber the current selection.
  const onSelectDate = useCallback((date: Date) => {
    setDraftDate(date);
  }, []);

  const onConfirm = useCallback(() => {
    if (!draftDate) return;
    const dateString = formatDateString(draftDate);
    dispatch(selectAvailableDate({ id: widgetId, value: dateString }));
    track('productAdderDateSelected', {
      productId: productId ?? '',
      productName: product?.name ?? '',
      date: dateString,
    });
    trackStepConfirmed(widgetId, productId ?? '', 'calendar');
    dispatch(goToNextStep(widgetId));
    setOpen(false);
  }, [draftDate, productId, product?.name, widgetId, dispatch]);

  const onOpenChange = useCallback((next: boolean) => setOpen(next), []);

  const dayRenderer = useCallback<UseDateSelectorReturn['dayRenderer']>(
    (cellProps) => {
      const day = cellProps.day as Date;
      const dateString = formatDateString(day);
      const match = mergedVisibleMonth.find((entry) => entry.date.dateString === dateString);
      // For days outside the visible interval (overflow cells from prev/next month rendered by MUI),
      // synthesize a fresh IntervalDate from the actual day so classifyDay returns the correct
      // `when` (past/today/future) and dateString instead of leaking the first visible day's metadata.
      const [y, m, d] = dateToObj(day);
      const intervalDate = match?.date ?? newIntervalDate(y, m, d);
      const classification = classifyDay({
        intervalDate,
        availables: match?.availableSchedulings,
        unavailables: match?.unavailableSchedulings,
        tariffPreviewMode: featuredTariffPreviewMode,
      });
      return React.createElement(Cell, {
        cellProps,
        classification,
        onSelect: () => onSelectDate(day),
        pending,
      });
    },
    [mergedVisibleMonth, featuredTariffPreviewMode, onSelectDate, pending],
  );

  return {
    placeholder,
    selectedDateFormatted,
    disabled: !productId,
    open,
    onOpenChange,
    pickerValue: draftDate,
    referenceDate: tupleToDate(currentMonthTuple),
    pending,
    onMonthChange,
    onSelectDate,
    onConfirm,
    dayRenderer,
    legend,
    totalLabel,
  };
}
